import React, { useState } from 'react';
import { ArrowLeft, Heart, MapPin, Clock, Zap, Bookmark, Share2 } from 'lucide-react';
import { DatePlan } from '../types';

interface ResultsProps {
  plans: DatePlan[];
  onSelectPlan: (plan: DatePlan) => void;
  onSavePlan: (plan: DatePlan) => void;
  onBackHome: () => void;
}

export default function Results({ plans, onSelectPlan, onSavePlan, onBackHome }: ResultsProps) {
  const [savedPlans, setSavedPlans] = useState<Set<string>>(new Set());
  const [comparingPlans, setComparingPlans] = useState<boolean>(false);

  const toggleSave = (planId: string, plan: DatePlan) => {
    if (savedPlans.has(planId)) {
      setSavedPlans(prev => {
        const next = new Set(prev);
        next.delete(planId);
        return next;
      });
    } else {
      setSavedPlans(prev => new Set([...prev, planId]));
      onSavePlan(plan);
    }
  };

  const costPerPerson = (plan: DatePlan) => Math.round(plan.estimatedCost / 2);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <button onClick={onBackHome} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={24} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Your Date Plans</h1>
            <p className="text-xs text-slate-500">{plans.length} options generated</p>
          </div>
        </div>
        <button
          onClick={() => setComparingPlans(!comparingPlans)}
          className="text-sm font-medium text-rose-600 hover:text-rose-700"
        >
          {comparingPlans ? '✓ Done' : 'Compare'}
        </button>
      </div>

      {/* Comparison View */}
      {comparingPlans && plans.length > 1 && (
        <div className="px-4 py-4 bg-rose-50 border-b border-rose-200">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-rose-200">
                  <th className="text-left py-2 px-2 font-bold text-rose-900">Aspect</th>
                  {plans.slice(0, 3).map(p => (
                    <th key={p.id} className="text-center py-2 px-2 font-bold text-rose-900">
                      {p.title.split(' ')[0]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-rose-100">
                  <td className="py-2 px-2 font-semibold">Duration</td>
                  {plans.slice(0, 3).map(p => (
                    <td key={p.id} className="text-center py-2 px-2">
                      {Math.round(p.totalDuration / 60)}h
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-rose-100">
                  <td className="py-2 px-2 font-semibold">Cost/Person</td>
                  {plans.slice(0, 3).map(p => (
                    <td key={p.id} className="text-center py-2 px-2">
                      ₹{costPerPerson(p)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-rose-100">
                  <td className="py-2 px-2 font-semibold">Stops</td>
                  {plans.slice(0, 3).map(p => (
                    <td key={p.id} className="text-center py-2 px-2">
                      {p.stops.length}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-rose-100">
                  <td className="py-2 px-2 font-semibold">Walking</td>
                  {plans.slice(0, 3).map(p => (
                    <td key={p.id} className="text-center py-2 px-2">
                      {p.totalWalking.toFixed(1)}km
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-2 px-2 font-semibold">Novelty</td>
                  {plans.slice(0, 3).map(p => (
                    <td key={p.id} className="text-center py-2 px-2">
                      {p.noveltyScore}/100
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Plans List */}
      <div className="px-4 pb-32 space-y-4 pt-4">
        {plans.map((plan, idx) => (
          <div
            key={plan.id}
            onClick={() => onSelectPlan(plan)}
            className="card-interactive"
          >
            {/* Badge */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex gap-2 flex-wrap">
                {plan.vibe.map(v => (
                  <span key={v} className="badge text-xs">
                    {v}
                  </span>
                ))}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSave(plan.id, plan);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Bookmark
                  size={20}
                  className={savedPlans.has(plan.id) ? 'fill-rose-600 text-rose-600' : 'text-slate-400'}
                />
              </button>
            </div>

            {/* Title & Description */}
            <h2 className="text-xl font-bold text-slate-900 mb-2">{plan.title}</h2>
            <p className="text-slate-600 text-sm mb-4 line-clamp-2">{plan.story}</p>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-2 mb-4 text-center text-xs">
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="font-bold text-slate-900">{Math.round(plan.totalDuration / 60)}h</div>
                <div className="text-slate-600 text-xs">Duration</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="font-bold text-slate-900">₹{costPerPerson(plan)}</div>
                <div className="text-slate-600 text-xs">Per person</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="font-bold text-slate-900">{plan.stops.length}</div>
                <div className="text-slate-600 text-xs">Stops</div>
              </div>
              <div className="bg-rose-100 rounded-lg p-2">
                <div className="font-bold text-rose-900">{plan.noveltyScore}</div>
                <div className="text-rose-700 text-xs">Novelty</div>
              </div>
            </div>

            {/* Quick Preview */}
            <div className="space-y-1">
              {plan.stops.slice(0, 3).map((stop, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                  <MapPin size={14} className="text-rose-600 flex-shrink-0" />
                  <span>{stop.place.name}</span>
                </div>
              ))}
              {plan.stops.length > 3 && (
                <div className="text-xs text-slate-500 ml-5">
                  + {plan.stops.length - 3} more stops
                </div>
              )}
            </div>

            {/* CTA */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelectPlan(plan);
              }}
              className="mt-4 w-full py-3 bg-rose-50 text-rose-600 font-semibold rounded-lg hover:bg-rose-100 transition-colors"
            >
              View Full Itinerary →
            </button>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {plans.length === 0 && (
        <div className="flex flex-col items-center justify-center min-h-96 px-4 text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">No routes found</h2>
          <p className="text-slate-600 mb-6">
            Try adjusting your preferences or budget
          </p>
          <button
            onClick={onBackHome}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
